/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import { oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LISTING_UNIT_TYPES,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_VAT,
} from '../../util/types';
import { buyerPrices, isVatRegisteredSeller } from '../../util/orderVatCore';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemCartItemsMaybe from './LineItemCartItemsMaybe';
import LineItemPackagePartsMaybe from './LineItemPackagePartsMaybe';
import LineItemServicesMaybe from './LineItemServicesMaybe';
import LineItemMiljoavgiftMaybe from './LineItemMiljoavgiftMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemVATMaybe from './LineItemVATMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemTotalPaidMaybe from './LineItemTotalPaidMaybe';
import LineItemVatIncludedMaybe from './LineItemVatIncludedMaybe';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * The buyer's line items with each item's VAT back inside its own row, and the
 * VAT line itself gone (P25009-475 BR-1/BR-2). Rows the server stores at full
 * price (freight, add-ons) come back unchanged, and so do refunds, so every
 * component below prints and sums exactly as before — only the amounts on the
 * item rows grow by their VAT, by as much as the VAT line shrinks to nothing.
 * The money itself is untouched: this is a copy for display.
 */
const withVatInRows = (lineItems, prices) => {
  const priced = new Map(prices.rows.map(row => [row.lineItem, row]));
  return lineItems
    .filter(item => item.reversal || item.code !== LINE_ITEM_VAT)
    .map(item => {
      const row = priced.get(item);
      if (!row || row.totalSubunits === item.lineTotal?.amount) return item;
      const { currency } = item.lineTotal;
      return {
        ...item,
        unitPrice: new Money(row.unitSubunits, currency),
        lineTotal: new Money(row.totalSubunits, currency),
      };
    });
};

/**
 * Whether the seller can read this order as "what the buyer paid, minus our
 * fee" (P25009-494 BR-8): every line the buyer paid is also on the seller's
 * side, and nothing has been refunded. Then the buyer's rows add up to the
 * payin, the payin minus the provider commission IS the payout, and every
 * figure on the page agrees.
 *
 * Not so for an order placed before P25009-467 (its VAT line is the buyer's
 * only, so the payin and the payout differ by more than the fee), an order with
 * a customer commission (not on in Console), or a refunded order (the payin
 * drops to what is left). Those keep the seller's net rows as before.
 */
const isSellerReceiptOrder = lineItems => {
  const buyerLines = lineItems.filter(item => item.includeFor.includes('customer'));
  return (
    buyerLines.length > 0 &&
    lineItems.every(item => !item.reversal) &&
    buyerLines.every(item => item.includeFor.includes('provider'))
  );
};

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    intl,
    dateType,
    timeZone,
    currency,
    marketplaceName,
    /* Optional. The checkout page passes it, because the transaction it
     * renders is speculative and carries no listing relationship of its own
     * (P25009-403); the payment page passes it for the listing's author (the
     * seller, P25009-475). Everywhere else the transaction's own listing is used. */
    listing,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const roleLineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));

  /* The buyer reads every price incl. VAT, with one "varav moms" under the
   * total for the whole order (P25009-475). Whether freight and montering
   * carry VAT depends on the seller: the order's provider once it exists, the
   * listing's author on the checkout preview, which has no provider yet —
   * whichever arrived with its profile. The seller's own view keeps its net
   * rows and VAT row (P25009-467). */
  const seller = [transaction.provider, listing?.author, transaction.listing?.author].find(
    user => user?.attributes?.profile
  );
  /* The seller reads the buyer's rows too — incl. VAT — then "Totalt betalt",
   * "varav moms", the fee and the payout (P25009-494 BR-8). Their item row used
   * to be the NET price (1 × 3 200 kr under a product card saying 4 000 kr),
   * with the VAT stacked under it as one more amount and no line saying what
   * the buyer actually paid. */
  const isSellerReceipt = isProvider && isSellerReceiptOrder(allLineItems);
  const buyerLineItems = isSellerReceipt
    ? allLineItems.filter(lineItem => lineItem.includeFor.includes('customer'))
    : roleLineItems;
  const buyerView =
    isCustomer || isSellerReceipt
      ? buyerPrices(buyerLineItems, { sellerChargesVat: isVatRegisteredSeller(seller) })
      : null;
  const lineItems = buyerView ? withVatInRows(buyerLineItems, buyerView) : roleLineItems;
  // The fee rows read the seller's own lines: the buyer's carry no commission.
  const feeLineItems = isSellerReceipt ? roleLineItems : lineItems;
  const lineItemCurrency = transaction.attributes.payinTotal?.currency || currency;

  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, item
  const lineItemUnitType = unitLineItem?.code;

  const hasCommissionLineItem = feeLineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  return (
    <div className={classes}>
      <LineItemBookingPeriod
        booking={booking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={timeZone}
      />

      <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} intl={intl} />
      {/* Directly under the package's own price line, so it reads as one item
        * with contents rather than a list of separate things (P25009-403). */}
      <LineItemPackagePartsMaybe transaction={transaction} listing={listing} intl={intl} />
      <LineItemCartItemsMaybe lineItems={lineItems} transaction={transaction} intl={intl} />
      <LineItemServicesMaybe lineItems={lineItems} transaction={transaction} intl={intl} />
      <LineItemMiljoavgiftMaybe lineItems={lineItems} intl={intl} />
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemVATMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      {isSellerReceipt ? (
        <>
          <LineItemTotalPaidMaybe transaction={transaction} intl={intl} />
          <LineItemVatIncludedMaybe
            vatSubunits={buyerView.vatSubunits}
            currency={lineItemCurrency}
            intl={intl}
            labelId="OrderBreakdown.vatOfTotalProvider"
          />
        </>
      ) : (
        <LineItemSubTotalMaybe
          lineItems={lineItems}
          code={lineItemUnitType}
          userRole={userRole}
          intl={intl}
          marketplaceCurrency={currency}
        />
      )}
      <LineItemRefundMaybe lineItems={lineItems} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={feeLineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={feeLineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemTotalPrice transaction={transaction} isProvider={isProvider} intl={intl} />
      {buyerView && isCustomer ? (
        <LineItemVatIncludedMaybe
          vatSubunits={buyerView.vatSubunits}
          currency={lineItemCurrency}
          intl={intl}
        />
      ) : null}

      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

OrderBreakdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  booking: null,
  dateType: null,
};

OrderBreakdownComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  userRole: oneOf(['customer', 'provider']).isRequired,
  transaction: propTypes.transaction.isRequired,
  booking: propTypes.booking,
  dateType: propTypes.dateType,
  // Only checkout and the payment page pass this; see the destructuring above.
  listing: propTypes.listing,

  // from injectIntl
  intl: intlShape.isRequired,
};

const OrderBreakdown = injectIntl(OrderBreakdownComponent);

OrderBreakdown.displayName = 'OrderBreakdown';

export default OrderBreakdown;
